import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  VdsIcon,
  VdsMenu,
  VdsMenuGroupHeader,
  VdsMenuItem,
  VdsMenuList,
  VdsSidenavItem,
} from '@volt/volt-extended-components-react';
import { Permission, userHasPermission } from '@customer/permissions';
import { useChangeSiteMutation } from '@customer/store/apis/authApi';
import { useAppSelector } from '@customer/store/hooks';

interface SiteSwitcherProps {
  isOpen: boolean;
}

export function SiteSwitcher({ isOpen }: SiteSwitcherProps) {
  const { t } = useTranslation();

  const userData = useAppSelector(state => state.auth);

  // Site context
  const [changeSite] = useChangeSiteMutation();

  const siteOptions = useMemo(() => {
    const onSelectSite = async (siteId: string) => {
      if (siteId !== userData.siteId) {
        await changeSite(siteId);
      }
    };

    if (userData.isLoggedIn && userData.siteContexts.length) {
      return userData.siteContexts.map(config => (
        <VdsMenuItem
          key={config.id}
          value={config.name}
          isActive={userData.siteId === config.id}
          onClicked={() => onSelectSite(config.id)}
          disabled={userData.siteChanging}
        />
      ));
    }

    return [];
  }, [
    userData.isLoggedIn,
    userData.siteContexts,
    userData.siteId,
    userData.siteChanging,
    changeSite,
  ]);

  const currentSiteName = useMemo<string>(() => {
    if (userData.isLoggedIn && userData.siteContexts.length && userData.siteId) {
      const matchedSite = userData.siteContexts.find(s => s.id === userData.siteId);
      if (matchedSite) {
        return matchedSite.name;
      }
    }

    return '';
  }, [userData.isLoggedIn, userData.siteContexts, userData.siteId]);

  if (userHasPermission(userData, Permission.SiteDataRead) && userData.siteContexts.length > 1) {
    return (
      <VdsMenu event="click" position="right">
        <VdsSidenavItem
          slot="menu-trigger"
          key="expandro2"
          icon="pin"
          expanded={isOpen}
          value={currentSiteName}
          isActive={false}
          hasChildren={false}
        >
          <VdsIcon
            slot="rightSlot"
            size="1.25rem"
            svgIcon="more-vertical"
            color="color-icon-medium"
          />
        </VdsSidenavItem>
        <div slot="menu-content">
          <VdsMenuList>
            <VdsMenuGroupHeader>{t('SelectASite')}</VdsMenuGroupHeader>
            {siteOptions}
          </VdsMenuList>
        </div>
      </VdsMenu>
    );
  } else {
    return null;
  }
}

export default SiteSwitcher;
